import { Table } from "containers/Table";
import { IColum } from "containers/Table/interfaces";
import { Button, Container, DivAcoes, Header, TextoHeader } from "./style";
import { FaDownload, FaEye, FaLock, FaLockOpen } from "react-icons/fa";
import usePermissoes from "hooks/usePermissoes";
import { useEffect, useState } from "react";
import { IListarLotesBloqueio } from "../../../../interfaces/IListarLotesBloqueio";
import { converterDataHoraBr } from "_utils/Data";
import { StatusLoteBloqueio } from "../StatusLoteBloqueio";
import { HttpResponse } from "_services/api/HttpResponse";
import { HttpError } from "_services/api/HttpError";
import { IData } from "_services/api/interface/HttpResponse";
import { toastErro } from "components/Toast";
import { utils, writeFileXLSX } from "xlsx";

interface IListarProps {
  dados: IListarLotesBloqueio[];
  handleLimitChange: (e: number) => void;
  handlePageChange: (e: number) => void;
  totalRows: number;
  loading: boolean;
  linhasPorPagina: number;
  handleVerDetalhes: (idLote: number) => void;
  relatorioLotes: () => Promise<HttpResponse<any, any> | HttpError<IData, any>>;
  onClickBloquear: () => void;
  onClickDesbloquear: () => void;
}

export function ListarLotesBloqueio({
  dados,
  handleLimitChange,
  handlePageChange,
  totalRows,
  loading,
  linhasPorPagina,
  handleVerDetalhes,
  relatorioLotes,
  onClickBloquear,
  onClickDesbloquear,
}: IListarProps) {
  const { listaPermissoes } = usePermissoes();
  const [showBtnBloquear, setShowBtnBloquear] = useState<boolean>(false);
  const [showBtnDesbloquear, setShowBtnDesbloquear] = useState<boolean>(false);

  useEffect(() => {
    if (!listaPermissoes || !Array.isArray(listaPermissoes)) {
      return;
    }

    if (
      listaPermissoes.some(
        (item) => item.nome_funcionalidade === "bloquear_veiculo",
      )
    ) {
      setShowBtnBloquear(true);
    }

    if (
      listaPermissoes.some(
        (item) => item.nome_funcionalidade === "desbloquear_veiculo",
      )
    ) {
      setShowBtnDesbloquear(true);
    }
  }, [listaPermissoes]);

  const columns: IColum<IListarLotesBloqueio>[] = [
    {
      name: "ID lote",
      selector: ({ id }: IListarLotesBloqueio) => id,
      width: "90px",
    },
    {
      name: "Empresa",
      selector: ({ nomeEmpresa }: IListarLotesBloqueio) => nomeEmpresa ?? "-",
    },
    {
      name: "CNPJ",
      selector: ({ cnpj }: IListarLotesBloqueio) => cnpj ?? "-",
    },
    {
      name: "Processos",
      selector: ({ qtdProcessos }: IListarLotesBloqueio) => qtdProcessos ?? "-",
      width: "100px",
    },
    {
      name: "Status",
      selector: ({ temConsultando, temErro }: IListarLotesBloqueio) => (
        <StatusLoteBloqueio
          status={
            temConsultando
              ? "processando"
              : temErro
                ? "itensComErro"
                : "concluido"
          }
        />
      ),
      width: "150px",
    },
    {
      name: "Data",
      selector: ({ dataCriacaoBloqueio }: IListarLotesBloqueio) =>
        dataCriacaoBloqueio ? converterDataHoraBr(dataCriacaoBloqueio) : "-",
      width: "250px",
    },
    {
      name: "Usuário responsável",
      selector: ({ usuarioResponsavel }: IListarLotesBloqueio) =>
        usuarioResponsavel ?? "-",
    },
    {
      name: "Ações",
      selector: ({ id }: IListarLotesBloqueio) => (
        <>
          <FaEye
            title="Visualizar"
            onClick={() => handleVerDetalhes(id)}
            style={{ cursor: "pointer" }}
          />
        </>
      ),
      width: "90px",
    },
  ];

  const onClickExportar = async () => {
    const { data, hasErro } = await relatorioLotes();

    if (hasErro) {
      toastErro(data?.message ?? "Não foi possível gerar o relatório");
      return;
    }

    const strategyStatus = {
      processando: "Processando",
      itensComErro: "Itens com erro",
      concluido: "Concluído",
    };

    const dadosPlanilha = data.itens.map((item: IListarLotesBloqueio) => {
      return {
        "ID lote": item?.id,
        Empresa: item?.nomeEmpresa ?? "-",
        CNPJ: item?.cnpj ?? "-",
        Processos: item?.qtdProcessos ?? 0,
        Status:
          strategyStatus[
            item?.temConsultando
              ? "processando"
              : item?.temErro
                ? "itensComErro"
                : "concluido"
          ],
        Data: item?.dataCriacaoBloqueio
          ? converterDataHoraBr(item.dataCriacaoBloqueio)
          : "-",
        "Usuário responsável": item?.usuarioResponsavel ?? "-",
      };
    });

    const planilha = utils.json_to_sheet(dadosPlanilha);
    planilha["!cols"] = [
      { wch: 7 },
      { wch: 20 },
      { wch: 20 },
      { wch: 7 },
      { wch: 15 },
      { wch: 15 },
      { wch: 20 },
    ];
    const workBook = utils.book_new();
    utils.book_append_sheet(workBook, planilha, "Lotes");

    const hoje = new Date();
    const dia = String(hoje.getDate()).padStart(2, "0");
    const mes = String(hoje.getMonth() + 1).padStart(2, "0");
    const ano = hoje.getFullYear();
    const nomeArquivo = `Lotes_Bloqueio_Administrativo_${dia}-${mes}-${ano}.xlsx`;
    writeFileXLSX(workBook, nomeArquivo);
  };

  return (
    <Container>
      <Header>
        <TextoHeader>Lotes criados</TextoHeader>
        <DivAcoes>
          <Button outline variante="primary" onClick={onClickExportar}>
            <FaDownload /> Exportar
          </Button>
          {showBtnDesbloquear && (
            <Button
              outline
              variante="primary"
              onClick={onClickDesbloquear}
              style={{ width: "190px" }}
            >
              <FaLockOpen /> Desbloquear veículos
            </Button>
          )}
          {showBtnBloquear && (
            <Button onClick={onClickBloquear} style={{ width: "190px" }}>
              <FaLock /> Bloquear veículos
            </Button>
          )}
        </DivAcoes>
      </Header>
      <hr />
      <Table
        keyField="id"
        totalRows={totalRows}
        pagination={true}
        columns={columns}
        data={dados}
        paginationRowsPerPageOptions={[30, 50, 100, 300]}
        loading={loading}
        onChangeRowsPerPage={handleLimitChange}
        onChangePage={handlePageChange}
        noSubHeader
        possuiTooltip={false}
        rowsPerPage={linhasPorPagina}
        headerFixo
      />
    </Container>
  );
}
